import { useEffect, useState } from "react";
import { Clock } from "lucide-react";

const fmt = (v) => {
  if (!v) return "";
  const [H, M] = v.split(":").map(Number);
  return `${H % 12 || 12}:${String(M).padStart(2, "0")}`;
};
const sideOf = (v) => (v && Number(v.split(":")[0]) >= 12 ? "PM" : "AM");
const build = (h, m, ap) => `${String((h % 12) + (ap === "PM" ? 12 : 0)).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

const read = (s) => {
  const m = s.trim().toLowerCase().replace(/\./g, "").match(/^(\d{1,2})(?:[:\s]?(\d{2}))?\s*([ap])?m?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2] || 0);
  if (h < 1 || h > 12 || mi > 59) return null;
  return { h, mi, ap: m[3] && (m[3] === "p" ? "PM" : "AM") };
};

export function TimePicker({ value, onChange, testId }) {
  const [text, setText] = useState(fmt(value));
  const [bad, setBad] = useState(false);
  const [side, setSide] = useState(sideOf(value));

  useEffect(() => { setText(fmt(value)); setBad(false); if (value) setSide(sideOf(value)); }, [value]);

  const commit = (t, ap) => {
    if (!t.trim()) { setBad(false); onChange(""); return; }
    const r = read(t);
    if (!r) { setBad(true); return; }
    const s = r.ap || ap;
    setSide(s);
    setText(`${r.h}:${String(r.mi).padStart(2, "0")}`);
    setBad(false);
    onChange(build(r.h, r.mi, s));
  };

  const pick = (ap) => {
    setSide(ap);
    if (text.trim() && read(text)) commit(text.replace(/\s*[ap]\.?m?\.?$/i, ""), ap);
  };

  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex w-full min-w-0 items-center gap-2">
        <div className={`flex h-10 min-w-0 flex-1 items-center gap-2 rounded-md border bg-white px-3 transition-colors focus-within:ring-2 focus-within:ring-primary/30 ${bad ? "border-red-400" : "border-input"}`}>
          <Clock className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            type="text"
            inputMode="numeric"
            value={text}
            placeholder="9:30"
            maxLength={8}
            onChange={(e) => { setText(e.target.value); setBad(false); }}
            onBlur={() => commit(text, side)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(text, side); } }}
            className="w-full min-w-0 bg-transparent text-[15px] outline-none placeholder:text-slate-400"
            aria-label="Time"
            data-testid={`${testId}-input`}
          />
        </div>
        <div className="flex h-10 shrink-0 rounded-full bg-secondary p-1" role="group" aria-label="AM or PM">
          {["AM", "PM"].map((ap) => (
            <button
              key={ap}
              type="button"
              aria-pressed={side === ap}
              onClick={() => pick(ap)}
              className={`w-12 rounded-full text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-[background-color,color,box-shadow] ${side === ap ? "bg-primary text-white shadow-sm" : "text-slate-500 hover:text-primary"}`}
              data-testid={`${testId}-${ap.toLowerCase()}`}
            >
              {ap}
            </button>
          ))}
        </div>
      </div>
      {bad && <p className="text-xs text-red-600" data-testid={`${testId}-error`}>Type a time like 9:30</p>}
    </div>
  );
}
